import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import { formatCurrency } from '@/shared/lib/currency';
import type { LosChartData } from '@/widgets/length-of-stay-chart';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { merge } from 'chart.js/helpers';
import type { FC } from 'react';
import { Scatter } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type ScatterPoint = {
  x: number;
  y: number;
  meta: LosChartData;
};

const backgroundBandsPlugin: Plugin<'scatter'> = {
  id: 'backgroundBands',
  beforeDraw: (chart: ChartJS) => {
    const {
      ctx,
      chartArea: { left, right },
      scales: { y },
    } = chart;

    const { min, max } = y;

    const bands = [
      {
        from: 10,
        to: max,
        color: chartColorPinned('--color-polar-green-1', '#E9FFD5'),
        label: 'Above Average',
        labelColor: chartColor('--color-polar-green-5'),
      },
      {
        from: -10,
        to: 10,
        color: chartColor('--color-neutral-2'),
        label: 'On Target',
        labelColor: chartColor('--color-neutral-8'),
      },
      {
        from: min,
        to: -10,
        color: chartColor('--color-dust-red-1'),
        label: 'Below Average',
        labelColor: chartColor('--color-dust-red-8'),
      },
    ];

    bands.forEach(({ from, to, color, labelColor, label }) => {
      const yFrom = y.getPixelForValue(from);
      const yTo = y.getPixelForValue(to);

      ctx.save();
      ctx.fillStyle = color;
      ctx.fillRect(left, yTo, right - left, yFrom - yTo);
      ctx.restore();

      ctx.save();
      ctx.fillStyle = labelColor;
      ctx.font = '12px Work Sans';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const midY =
        yTo +
        (yFrom - yTo) / 2 +
        (label === 'Above Average' ? -10 : label === 'Below Average' ? 10 : 0);
      ctx.fillText(label, right - 8, midY);
      ctx.restore();
    });
  },
};

type LengthOfStayChartProps = {
  data: ChartData<'scatter'>;
  options?: ChartOptions<'scatter'>;
  baselineLabel?: string;
};

export const LengthOfStayScatterChart: FC<LengthOfStayChartProps> = ({
  data,
  options,
  baselineLabel = 'Median',
}) => {
  const defaultOptions: ChartOptions<'scatter'> = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        offset: true,
        min: 1,
        position: 'right',
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          stepSize: 1,
          padding: 12,
          color: chartColorPinned('--color-neutral-11', '#262626'),
          font: {
            family: 'Work Sans',
            size: 12,
          },
          callback: function (value) {
            const labels = this.chart.data.labels as string[] | undefined;

            if (!labels) return '';

            const index = Math.round(Number(value)) - 1;
            return labels[index] ?? '';
          },
        },
      },
      y: {
        min: -50,
        max: 50,
        ticks: {
          color: chartColor('--color-neutral-8'),
          font: {
            family: 'Work Sans',
          },
          stepSize: 10,
          callback: (value) => {
            return `${value}%`;
          },
        },
        position: 'left',
        grid: {
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        cornerRadius: 2,
        backgroundColor: chartColor('--color-neutral-1'),
        bodyColor: chartColorPinned('--color-neutral-8', '#4d4d4d'),
        borderColor: chartColorPinned('--color-neutral-5', '#e6e6e6'),
        borderWidth: 1,
        bodyFont: {
          size: 14,
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            const point = context.raw as ScatterPoint;
            const { fullName, rate, payPosition } = point.meta;

            const sign = payPosition < 0 ? '' : '+';

            return [
              fullName,
              `Rate: ${formatCurrency(rate)}`,
              `Pay Position: ${sign}${payPosition}% vs ${baselineLabel}`,
            ];
          },
        },
      },
    },
    onClick: (event, _elements, chart) => {
      const elements = chart.getElementsAtEventForMode(
        event.native as Event,
        'nearest',
        { intersect: true },
        false
      );

      if (elements.length > 0) {
        const el = elements[0];
        const point = chart.data.datasets[el.datasetIndex]?.data[el.index] as
          | ScatterPoint
          | undefined;

        if (point?.meta?.residentId) {
          window.location.href = `/residents/${point.meta.residentId}`;
        }
      }
    },
    onHover: (event, elements) => {
      const canvas = event.native?.target as HTMLCanvasElement;

      if (canvas) {
        canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
  };

  const mergedOptions = merge(defaultOptions, options);

  return (
    <Scatter
      className="w-full"
      data={data}
      options={mergedOptions}
      plugins={[backgroundBandsPlugin]}
    />
  );
};
